/**
 * The service template editor's four pages (RUT-4306).
 *
 * One home per control: each template field belongs to exactly one card, and
 * each card to exactly one page. The nav dot, the Edited tag, deep links and
 * the page summaries all read from this map, so moving a control is a one-line
 * change here (and `pages.test.ts` proves nothing was dropped or doubled).
 */
import {
    BillingMethod,
    PricingMethod,
    ServiceType,
    formatCadenceLabel,
    resolveEffectiveReviewCadence,
    resolveTemplateCustomerVisibility,
    type ServiceTemplate,
} from '@ruta/shared';

export type PageId = 'offer' | 'price' | 'visit' | 'extras';

export type CardId =
    | 'identity'
    | 'visibility'
    | 'stopSelling'
    | 'charging'
    | 'rates'
    | 'schedule'
    | 'onSite'
    | 'review'
    | 'after'
    | 'followUps'
    | 'subPay'
    | 'addons'
    | 'links';

export interface PageMeta {
    id: PageId;
    title: string;
    /** One line under the page title: what the page holds and what a change does. */
    intro: string;
}

export const PAGES: PageMeta[] = [
    {
        id: 'offer',
        title: 'Offer',
        intro:
            'What this service is called, how it reads in the portal, and who can book it. Nothing here changes what anyone pays.',
    },
    {
        id: 'price',
        title: 'Price & schedule',
        intro:
            'How this service is charged and when it runs. When you publish changes from this page, you choose whether existing customers are repriced.',
    },
    {
        id: 'visit',
        title: 'The visit',
        intro:
            'What the crew does and what happens when they finish. Everything here applies from the next visit, including visits already on the calendar. Finished visits stay as they were, and no rates change.',
    },
    {
        id: 'extras',
        title: 'Add-ons & links',
        intro:
            "Extras customers can pick, and how this service relates to your other services. You don't need these to start selling.",
    },
];

export const PAGE_CARDS: Record<PageId, CardId[]> = {
    offer: ['identity', 'visibility', 'stopSelling'],
    price: ['charging', 'rates', 'schedule'],
    visit: ['onSite', 'review', 'after', 'followUps', 'subPay'],
    extras: ['addons', 'links'],
};

/** Every stored field a card's controls write. */
export const CARD_FIELDS: Record<CardId, string[]> = {
    identity: ['name', 'category', 'sortOrder', 'slug', 'description', 'detailedDescription', 'images'],
    visibility: ['customerVisible', 'visibleToActiveSubscribers', 'isCoreService', 'rateHiddenMessage'],
    stopSelling: ['isActive'],
    charging: [
        'billingMethod',
        'pricingMethod',
        'volumePricing',
        'visitPricing',
        'isTaxable',
        'rateRounding',
        'roundUpToCents',
    ],
    rates: ['hourlyRate', 'builtInHours', 'minimumCharge', 'packagePricing'],
    schedule: [
        'serviceType',
        'allowOneTimeService',
        'scheduleConfig',
        'isYearRound',
        'periodsSelectable',
        'allowCustomerStartDate',
        'dayPreferences',
        'periods',
    ],
    onSite: ['allowCrewLeaderDispatch', 'photoRequirement', 'requireProofOfService', 'weatherSensitivity'],
    review: ['reviewCadence', 'firstVisitAutoPricing', 'notifyCustomerOnReview'],
    after: ['autoSharePhotos', 'showLaborHoursOnInvoice', 'completionInstructions'],
    followUps: ['actions'],
    subPay: ['subcontractorRateOverrides'],
    addons: ['serviceOptions'],
    links: ['dependencies', 'relatedServices', 'pairedServices', 'replacesServices', 'requiresAnyCoreService'],
};

/** DOM id of each card. Old section ids are kept so `#sec-*` scroll targets survive. */
export const CARD_DOM_ID: Record<CardId, string> = {
    identity: 'sec-identity',
    visibility: 'sec-visibility',
    stopSelling: 'sec-lifecycle',
    charging: 'sec-pricing',
    rates: 'sec-rates',
    schedule: 'sec-scheduling',
    onSite: 'sec-delivery',
    review: 'sec-automation',
    after: 'sec-after',
    followUps: 'sec-followups',
    subPay: 'sec-subcontractors',
    addons: 'sec-addons',
    links: 'sec-relationships',
};

/** Labels for the publish dialog and the rail's Unpublished list. */
export const FIELD_LABELS: Record<string, string> = {
    billingMethod: 'Billing method',
    pricingMethod: 'Pricing method',
    hourlyRate: 'Hourly rate',
    minimumCharge: 'Minimum charge',
    builtInHours: 'Built-in hours',
    volumePricing: 'Volume pricing',
    visitPricing: 'Visit pricing',
    packagePricing: 'Package pricing',
    roundUpToCents: 'Round-up',
    rateRounding: 'Round-up',
    isYearRound: 'Year-round',
    isTaxable: 'Taxable',
    isActive: 'Active',
    isCoreService: 'Core service',
    customerVisible: 'Who sees this service',
    visibleToActiveSubscribers: 'Who sees this service (existing customers)',
    scheduleConfig: 'Frequency',
    serviceType: 'Cadence',
    allowOneTimeService: 'Cadence (one-time)',
    periodsSelectable: 'Customer-selectable periods',
    periods: 'Service periods',
    allowCustomerStartDate: 'Let customers request a start date',
    dayPreferences: 'Day preferences',
    allowCrewLeaderDispatch: 'Crew leader dispatch',
    subcontractorRateOverrides: 'Subcontractor rate overrides',
    requiresAnyCoreService: 'Require any core service',
    relatedServices: 'Prefer same crew as',
    replacesServices: 'Turning this on turns off',
    pairedServices: 'Scheduled together with',
    dependencies: 'Customer must have first',
    serviceOptions: 'Add-on options',
    actions: 'Follow-ups',
    reviewCadence: 'Review visits',
    weatherSensitivity: 'Weather sensitivity',
    firstVisitAutoPricing: 'First-visit pricing',
    notifyCustomerOnReview: 'Notify customer in review',
    photoRequirement: 'Photo requirement',
    requireProofOfService: 'Proof of service',
    autoSharePhotos: 'Share photos with customer',
    showLaborHoursOnInvoice: 'Labor hours on invoice',
    completionInstructions: 'Completion instructions',
    rateHiddenMessage: 'Pre-rate release note',
    detailedDescription: 'Detailed description',
    sortOrder: 'Sort order',
    slug: 'Service link',
    images: 'Service images',
    name: 'Name',
    category: 'Category',
    description: 'Description',
};

export function formatFieldName(field: string): string {
    return (
        FIELD_LABELS[field] ??
        field.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase())
    );
}

const CARD_OF_FIELD = new Map<string, CardId>(
    (Object.entries(CARD_FIELDS) as [CardId, string[]][]).flatMap(([card, fields]) =>
        fields.map((f) => [f, card] as const),
    ),
);

const PAGE_OF_CARD = new Map<CardId, PageId>(
    (Object.entries(PAGE_CARDS) as [PageId, CardId[]][]).flatMap(([page, cards]) =>
        cards.map((c) => [c, page] as const),
    ),
);

export function cardForField(field: string): CardId | undefined {
    return CARD_OF_FIELD.get(field);
}

export function pageForField(field: string): PageId | undefined {
    const card = cardForField(field);
    return card ? PAGE_OF_CARD.get(card) : undefined;
}

/** Old section anchors (before RUT-4306) and the new page ids. */
const HASH_TO_PAGE: Record<string, PageId> = {
    identity: 'offer',
    visibility: 'offer',
    lifecycle: 'offer',
    pricing: 'price',
    scheduling: 'price',
    delivery: 'visit',
    automation: 'visit',
    relationships: 'extras',
    addons: 'extras',
    offer: 'offer',
    price: 'price',
    visit: 'visit',
    extras: 'extras',
};

export function pageForHash(hash: string): PageId | null {
    const key = hash.replace(/^#/, '');
    return HASH_TO_PAGE[key] ?? null;
}

/** Pages holding a staged change. An unmapped field counts against Offer. */
export function stagedPages(fields: Iterable<string>): Set<PageId> {
    const pages = new Set<PageId>();
    for (const f of fields) pages.add(pageForField(f) ?? 'offer');
    return pages;
}

export function stagedCards(fields: Iterable<string>): Set<CardId> {
    const cards = new Set<CardId>();
    for (const f of fields) {
        const card = cardForField(f);
        if (card) cards.add(card);
    }
    return cards;
}

const BILLING_LABEL: Record<string, string> = {
    [BillingMethod.PER_VISIT]: 'Per visit',
    [BillingMethod.PER_HOUR]: 'Per hour',
    [BillingMethod.PACKAGE]: 'Package',
    [BillingMethod.CONSUMABLE]: 'Consumable',
};

function shortMoney(n: number): string {
    return Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`;
}

/** True when the template's hourly rate is the number the price is built from. */
export function usesHourlyRate(t: Pick<ServiceTemplate, 'billingMethod' | 'pricingMethod'>): boolean {
    return (
        t.billingMethod === BillingMethod.PER_HOUR ||
        t.billingMethod === BillingMethod.PACKAGE ||
        (t.billingMethod === BillingMethod.PER_VISIT && t.pricingMethod === PricingMethod.BILLABLE_LABOR_RATE)
    );
}

function frequencyLabel(t: ServiceTemplate): string | null {
    if (t.serviceType === ServiceType.ONE_TIME) return 'One time';
    const freq = t.scheduleConfig?.frequency;
    return freq ? formatCadenceLabel(freq) : null;
}

/** The live one-line summary under each nav card. */
export function pageSummary(page: PageId, t: ServiceTemplate): string {
    switch (page) {
        case 'offer': {
            const v = resolveTemplateCustomerVisibility(t);
            if (v === 'everyone') return 'Everyone can book it';
            if (v === 'existing') return 'Current customers only';
            return 'Hidden from customers';
        }
        case 'price': {
            const rate =
                usesHourlyRate(t) && typeof t.hourlyRate === 'number' && t.hourlyRate > 0
                    ? `${shortMoney(t.hourlyRate)}/hr`
                    : null;
            return [BILLING_LABEL[t.billingMethod] ?? null, rate, frequencyLabel(t)]
                .filter(Boolean)
                .join(' · ');
        }
        case 'visit': {
            const review = resolveEffectiveReviewCadence(t);
            const reviewText =
                review?.mode === 'every-visit'
                    ? 'every visit'
                    : review?.mode === 'first-x-visits'
                        ? `first ${review.x}`
                        : 'off';
            const photos = t.photoRequirement;
            const photoText =
                photos?.mode === 'every-visit'
                    ? 'every visit'
                    : photos?.mode === 'every-n-visits'
                        ? `every ${photos.n}`
                        : 'off';
            return `Review: ${reviewText} · Photos: ${photoText}`;
        }
        case 'extras': {
            const addons = t.serviceOptions?.length ?? 0;
            const links =
                (t.dependencies?.length ?? 0) +
                (t.relatedServices?.length ?? 0) +
                (t.pairedServices?.length ?? 0) +
                (t.replacesServices?.length ?? 0);
            return `${addons} add-on${addons === 1 ? '' : 's'} · ${links} linked service${links === 1 ? '' : 's'}`;
        }
    }
}
